import { get } from 'lodash'
import { runInAction } from 'mobx'
import rootStore from '../..'
import { getCompactLst } from '../../../api/utils/api-compact'
import { wrapWithLoading } from '../../../config/general-actions'
import { responseHasError } from '../../users/actions'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import { requireMonthlyReportsById } from '../../monthly-reports/actions'
import dayjs from 'dayjs'
import { deleteLstItem } from '../../../api/utils/api-delete'

const uiLstMonthlyReportsStore = rootStore.uiLstMonthlyReports

/**
 * @async
 * @function actLoadMonthlyReports
 * @description Asynchronously loads reports from the server and updates the UI list store.
 * @param { function } navigate - The navigation function.
 * @returns {Promise<void>}
 */
export const actLoadMonthlyReports = wrapWithLoading(
	async (get_modified = false) => {
		const params = {
			// to complete
			del: uiLstMonthlyReportsStore.del,
			pg: uiLstMonthlyReportsStore.pg,
			rpg: uiLstMonthlyReportsStore.rpg,
			search: uiLstMonthlyReportsStore.search,
			// category_id: uiLstMonthlyReportsStore.filter?.category || '',
			progress: uiLstMonthlyReportsStore.filter?.progress || '',
			user_uid: uiLstMonthlyReportsStore.filter?.user_uid || '',
			get_modified: get_modified,
			orderBy: uiLstMonthlyReportsStore.filter?.orderBy,
			orderDir: uiLstMonthlyReportsStore.filter?.orderDir
		}

		const _response = await getCompactLst('monthly_reports', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success' && !get_modified) {
			console.log('actLoadMonthlyReports() response:', _response)
			// to complete
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else if (
			!hasError &&
			_response.status === 'success' &&
			get_modified
		) {
			// to complete
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Monthly Reports' }
)

/**
 * @function actUndoOrDeleteReport
 * @description Flags a monthly report for deletion or undoes the deletion based on the `status`. Handles demo mode separately by directly modifying the store.
 * @param {string} reportId - ID of the monthly report to be deleted or restored.
 * @param {boolean} status - If true, schedules for deletion; if false, cancels deletion.
 * @param {boolean} demo - If true, performs a mock delete without server request.
 * @returns {Promise<boolean|void>} Returns true immediately if in demo mode; otherwise awaits server response.
 */
export const actUndoOrDeleteReport = wrapWithLoading(
	async (reportId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				uiLstMonthlyReportsStore.setIds(
					uiLstMonthlyReportsStore.ids.filter((id) => id !== reportId)
				)
				uiLstMonthlyReportsStore.setNr(uiLstMonthlyReportsStore.nr - 1)
			})

			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['monthly_reports'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('monthly_reports', {
			id: reportId,
			to_delete: timestamp || false
		})
		const _storeItm = requireMonthlyReportsById(reportId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)

				uiLstMonthlyReportsStore.setIds(
					uiLstMonthlyReportsStore.ids.filter((id) => id !== reportId)
				)
				uiLstMonthlyReportsStore.setNr(uiLstMonthlyReportsStore.nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Monthly Report' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireMonthlyReportsById(_srvItm.report_id)
			_storeItm.updateFromServer(_srvItm)
		}
		uiLstMonthlyReportsStore.setIds(srvItms.map((it) => it.report_id))
		uiLstMonthlyReportsStore.setNr(nr)
	})
}
